import { useMemo, useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import { XIcon, ExternalLinkIcon, BugIcon, ListEndIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CopyButton } from "@/components/ui/copy-button";
import { cn } from "@/lib/utils";
import { ResizableHandle, ResizablePanel } from "@/components/ui/resizable";
import {
  Drawer,
  DrawerContent,
} from "@/components/ui/drawer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { useUser, useAuth } from "@clerk/react";
import { toast } from "sonner";
import CreatorInbox from "@/components/dashboard_campaign/inbox/CreatorInbox";
import AutopilotToggle from "@/components/dashboard_campaign/inbox/AutopilotToggle";
import { CreatorRightPanelProps } from "./types";
import { useCreatorsStore, useInboxStore } from "@/stores";
import { useIsMobile } from "@/hooks/use-mobile";
import { useCreatorsTableContext } from "./CreatorsTableContext";
import { PhasePipelineBreadcrumbs } from "./phase/blocks";
import { formatThreadForPrompt } from "@/utils/emailFormatters";

const VITE_ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT;
const isDev = VITE_ENVIRONMENT !== "production";

// Thread panel share of the split (percent).
const PANEL_DEFAULT_SIZE = 45;
const PANEL_MIN_SIZE = 40;

export default function CreatorRightPanel({
  selectedCreatorId,
  onClose,
}: CreatorRightPanelProps) {
  const isMobile = useIsMobile();
  const { user } = useUser();
  const { getToken } = useAuth();
  const { creators } = useCreatorsStore();
  const { inbox, isLoading: inboxLoading } = useInboxStore();
  const { setPhaseDialog, campaignFor } = useCreatorsTableContext();

  const [devDialogOpen, setDevDialogOpen] = useState(false);
  const [devMsgType, setDevMsgType] = useState("");
  const [devSubmitting, setDevSubmitting] = useState(false);

  const open = !!selectedCreatorId;

  const handleDevEnqueue = async () => {
    if (!selectedCreatorId || !devMsgType.trim()) return;
    setDevSubmitting(true);
    try {
      const token = await getToken();
      const res = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/api/campaigns/devEnqueueOutreach`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            campaign_creator_id: selectedCreatorId,
            msg_type: devMsgType.trim(),
          }),
        }
      );
      const json = await res.json();
      if (json.success) {
        toast.success(json.message ?? "Task enqueued");
        setDevDialogOpen(false);
        setDevMsgType("");
      } else {
        toast.error(json.error ?? "Failed to enqueue task");
      }
    } catch (err) {
      toast.error(`Failed: ${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setDevSubmitting(false);
    }
  };

  const currentCreator = creators.find((c) => c._id === selectedCreatorId);
  // The open creator's own campaign (mailbox, pipeline) — org-wide the panel
  // serves rows from many campaigns.
  const campaign = campaignFor(currentCreator);
  const threadId = currentCreator?.thread_id;
  const creatorEmail = currentCreator?.email;

  // The loaded thread as prompt-ready text; empty until the thread is in.
  const threadText = useMemo(
    () =>
      currentCreator && inbox.length > 0
        ? formatThreadForPrompt({
            messages: inbox,
            campaignName: campaign?.campaign_name,
            creator: currentCreator,
            ourEmails: [campaign?.campaign_owner?.email, ...(campaign?.eve_mailboxes ?? []).map((m) => m.email)],
            unsentDraft: currentCreator.next_message,
          })
        : "",
    [inbox, currentCreator, campaign]
  );

  const senderAccount = user?.externalAccounts?.find(
    (acc) => acc.id === campaign?.external_account_id
  );
  const isGmail = senderAccount?.provider === "google";
  const isOutlook = senderAccount?.provider === "microsoft";

  const mailUrl = (() => {
    if (!threadId) return null;
    if (isGmail) {
      return {
        url: `https://mail.google.com/mail/?authuser=${senderAccount?.emailAddress || "0"}#all/thread-f:${BigInt(`0x${threadId}`)}`,
        label: "Open in Gmail",
      };
    }
    if (isOutlook && creatorEmail) {
      return {
        url: `https://outlook.live.com/mail/0/search?query=${encodeURIComponent(creatorEmail)}`,
        label: "Open in Outlook",
      };
    }
    return null;
  })();

  // Stable header element: a fresh <PhasePipelineBreadcrumbs> per render
  // re-rendered the entire inbox on every panel state flip (animating on/off).
  const inboxHeader = useMemo(
    () =>
      currentCreator ? (
        <PhasePipelineBreadcrumbs
          campaign={campaign}
          creator={currentCreator}
          onPhaseClick={(phaseKey) =>
            setPhaseDialog({ creatorId: currentCreator._id, phaseKey })
          }
          className="border-b border-dashed px-3 py-2"
        />
      ) : undefined,
    [campaign, currentCreator, setPhaseDialog]
  );

  const panelContent = selectedCreatorId && (
    // Flat inbox-tab shell: header strip on the border, conversation below —
    // strip height matches the table toolbar so the border-b lines up.
    <div className="group/inbox flex h-full flex-col bg-background">
      <div className="flex h-11 shrink-0 items-center gap-1 border-b px-2">
        <Button
          onClick={onClose}
          variant="ghost"
          size="icon-sm"
         
          title="Close Inbox"
        >
          <XIcon className="size-4" />
        </Button>

        {mailUrl && (
          <a href={mailUrl.url} target="_blank" rel="noopener noreferrer">
            <Button
              variant="ghost"
              className="h-7 px-2"
              size="sm"
              title={mailUrl.label}
            >
              <ExternalLinkIcon className="opacity-60" />
              {mailUrl.label}
            </Button>
          </a>
        )}


        {isDev && (
          <Button
            onClick={() => setDevDialogOpen(true)}
            variant="ghost"
            size="sm"
            className="h-7 px-2"
            title="Dev: enqueue outreach task"
          >
            <BugIcon className="opacity-60" />
            Dev:Enqueue
          </Button>
        )}

        <div className="flex-1" />

        {threadText && !inboxLoading && (
          <CopyButton
            buttonText=""
            variant="ghost"
            tooltipText="Copy thread as prompt-ready text"
            tooltipDelay={0}
            textToCopy={threadText}
            className={cn(
              "size-7 shrink-0 text-muted-foreground transition-non hover:bg-muted-foreground/10 hover:text-foreground",
              // Hover-revealed like the table's copy buttons; touch screens can't hover.
              !isMobile && "opacity-0 group-hover/inbox:opacity-100 focus-visible:opacity-100"
            )}
          />
        )}

        <AutopilotToggle creator_id={selectedCreatorId} />
      </div>

      <div className="flex-1 min-h-0 overflow-hidden">
        <CreatorInbox
          creator_id={selectedCreatorId}
          // Rides inside the inbox scroll area, styled like the subject row.
          header={inboxHeader}
          // Org-wide CRM has no open campaign in the store — the row's own
          // campaign decides whether the thread lives in Amy's mailbox.
          isEve={campaign?.is_eve === true}
        />
      </div>

      {isDev && (
        <AlertDialog open={devDialogOpen} onOpenChange={setDevDialogOpen}>
          <AlertDialogContent className="max-w-lg">
            <AlertDialogHeader>
              <AlertDialogTitle>Dev: Enqueue outreach task</AlertDialogTitle>
              <AlertDialogDescription>
                Sends <code>enqueue_outreach(campaign_creator_id, msg_type)</code> for the selected creator.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <Input
              autoFocus
              placeholder="msg_type"
              value={devMsgType}
              onChange={(e) => setDevMsgType(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && devMsgType.trim() && !devSubmitting) {
                  e.preventDefault();
                  handleDevEnqueue();
                }
              }}
            />
            <div className="flex flex-wrap gap-2">
              {["reply", "follow_up", "follow_up_reply", "initial_outreach", "pending-activation", "pending-rejection", "stage_outreach", "stage_reply", "stage_follow_up"].map((example) => (
                <Button
                  key={example}
                  variant="flat"
                  size="sm"
                  className="h-6 px-2 text-xs font-mono rounded-sm"
                  onClick={() => setDevMsgType(example)}
                >
                  {example}
                </Button>
              ))}
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={devSubmitting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  handleDevEnqueue();
                }}
                disabled={devSubmitting || !devMsgType.trim()}
              >
                {devSubmitting ? <Spinner /> : <ListEndIcon />}
                Enqueue Task
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={(nowOpen) => !nowOpen && onClose()}>
        <DrawerContent className="h-[95vh] pb-6">
          {panelContent}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <>
      <ResizableHandle
        withHandle
        className={open ? "hover:ring-2 active:ring-2 ring-primary/20 hover:bg-primary active:bg-primary transition-all duration-75" : "hidden"}
      />
      <ResizablePanel
        defaultSize={PANEL_DEFAULT_SIZE}
        minSize={PANEL_MIN_SIZE}
        className={open ? "border-r" : "hidden"}
      >
        {panelContent}
      </ResizablePanel>
    </>
  );
}
